import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { Partner } from './partner/partner';
import { PartnerAjaxResponse } from './partner/partner-ajax.response';

/**
 * Holds the current partner ID and partner metadata.
 *
 * The purpose of this V2 service is to enforce reactivity in the application. Different services and components don't
 * need to concern themselves with loading the current partner data, they can trigger the data to load by showing
 * intent via subscribing.
 *
 * TODO: This service will serve out-of-date cache data if the partner has been altered. Once this service owns updating
 * a partner, it can accurately clear the cache.
 *
 * @export
 * @class PartnerServiceV2
 */
@Injectable({
  providedIn: 'root',
})
export class PartnerServiceV2 {
  /**
   * The data for the active partner. If you subscribe to this, be sure to handle null, as this will trigger loading
   * the partner data if it has not already been loaded.
   *
   * @type {Observable<Partner|nulll>}
   * @memberof PartnerServiceV2
   */
  public readonly partner$: Observable<Partner | null> = this.partnerId$.pipe(
    switchMap((partnerId) => {
      return this.getPartnerByPartnerId(partnerId).pipe(
        map((result) => new Partner(result)),
        startWith(null),
      );
    }),
    shareReplay(1), // So that new subscriptions don't replay all the values and load the partner again.
  );

  public readonly partnerName$: Observable<string | null> = this.partner$.pipe(
    map((partner) => (partner ? partner.name : null)),
  );

  /**
   * Creates an instance of PartnerServiceV2.
   * @returns {PartnerServiceV2}
   * @memberof PartnerServiceV2
   */
  constructor(
    @Inject('PARTNER_ID') public readonly partnerId$: Observable<string>,
    private readonly http: HttpClient,
  ) {}

  private getPartnerByPartnerId(partnerId: string): Observable<PartnerAjaxResponse> {
    const url = `/_ajax/v1/partner/?partnerId=${partnerId}`;
    return this.http.get<PartnerAjaxResponse>(url);
  }
}
